import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { kvkNumberValidator } from '@next/arable-lib/shared';
import {
  AbstractDialogResultData,
  AbstractFormDialog,
  AbstractFormDialogData,
} from '@next/core-lib/dialog';
import { LocaleService } from '@next/core-lib/i18n';
import { SiteState } from '@next/core-lib/site';
import { SelectedCustomer, ShipToIdentification, UserSelectors } from '@next/core-lib/user';
import { select, Store } from '@ngrx/store';
import { Observable, ReplaySubject, Subscription } from 'rxjs';
import { filter, map, startWith, takeUntil } from 'rxjs/operators';
import {
  AgroConnectCustomer,
  AgroConnectExternalParty,
  RetrieveCropfieldsSource,
  RvoProvider,
} from 'src/app/shared/models';

import { LoadAgroConnectCustomersRequest } from '../../store/actions/crop-field.actions';
import { getAgroConnectCustomers } from '../../store/selectors/crop-field.selectors';

export interface RetrieveCropfieldsDialogModel extends AbstractFormDialogData {
  providers: string[];
  years: number[];
  provider?: string;
  year?: number;
  farmId?: string;
  showProviderField?: boolean;
}

@Component({
  selector: 'app-retrieve-cropfields-dialog',
  templateUrl: './retrieve-cropfields-dialog.component.html',
  styleUrls: ['./retrieve-cropfields-dialog.component.scss'],
  standalone: false,
})
export class RetrieveCropfieldsDialogComponent
  extends AbstractFormDialog<RetrieveCropfieldsSource>
  implements OnInit, OnDestroy
{
  data = inject<RetrieveCropfieldsDialogModel>(MAT_DIALOG_DATA);
  protected mdDialogRef =
    inject<
      MatDialogRef<
        RetrieveCropfieldsDialogComponent,
        AbstractDialogResultData<RetrieveCropfieldsSource>
      >
    >(MatDialogRef);
  private readonly fb = inject(FormBuilder);
  private readonly store = inject<Store<SiteState>>(Store);
  private readonly locale = inject(LocaleService);

  private subscription = new Subscription();
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);

  readonly showProviderField = this.data.showProviderField ?? true;

  formGroup = this.fb.group({
    provider: [this.data.provider, [Validators.required]],
    year: [this.data.year, [Validators.required]],
    externalCustomer: [this.data.farmId],
    farmId: [this.data.farmId, [Validators.required]],
  });

  customers$: Observable<AgroConnectCustomer[]> = this.store.pipe(
    select(getAgroConnectCustomers),
    takeUntil(this.destroyed$),
  );
  customers: AgroConnectCustomer[] = [];

  selectedCustomer$: Observable<SelectedCustomer> = this.store.pipe(
    select(UserSelectors.selectCustomer),
    takeUntil(this.destroyed$),
  );

  filteredCustomers$ = this.formGroup.controls.externalCustomer.valueChanges.pipe(
    startWith(''),
    filter((value) => typeof value === 'string'),
    map((value: string) =>
      value && value.trim()
        ? this.removeDuplicateCustomers([...this.filter(value, [...this.customers])])
        : this.customers,
    ),
  );

  withRvoProvider = false;

  ngOnInit(): void {
    this.subscription.add(
      this.formGroup.controls.provider.valueChanges.subscribe(() => this.updateCustomers()),
    );
    this.subscription.add(
      this.formGroup.controls.year.valueChanges.subscribe(() => this.updateCustomers()),
    );

    this.subscription.add(
      this.formGroup.controls.externalCustomer.valueChanges.subscribe((farmId) => {
        this.formGroup.controls.farmId.setValue(
          this.customers.find((customer) => customer.farmId === farmId) ? farmId : '',
        );
      }),
    );

    this.subscription.add(
      this.customers$.subscribe((customers) => {
        if (this.withRvoProvider) {
          return;
        }

        const provider = this.formGroup.controls.provider.value;
        const year = this.formGroup.controls.year.value;
        this.customers =
          provider && year
            ? [...customers].sort((a, b) =>
                !(a.farmDesignator && b.farmDesignator)
                  ? 0
                  : a.farmDesignator.toLowerCase() > b.farmDesignator.toLowerCase()
                    ? 1
                    : -1,
              )
            : [];

        this.formGroup.controls.externalCustomer.setValue('');
      }),
    );

    if (this.data.provider && this.data.provider in RvoProvider) {
      this.withRvoProvider = true;
      this.subscription.add(
        this.selectedCustomer$.subscribe((customer) => {
          this.prefillKvkNumberForRvo(customer);
          this.prefillCustomerNameForRvo(customer);
        }),
      );
    }
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  displayCustomer() {
    return (farmId?: string) => {
      if (!farmId) {
        return '';
      }

      return this.customers.find((customer) => customer.farmId === farmId)?.farmDesignator ?? '';
    };
  }

  confirmImportCropFields(result: RetrieveCropfieldsSource) {
    this.confirm(result);
  }

  getProviderName(provider: string): string {
    switch (provider) {
      case AgroConnectExternalParty.CropVision:
        return 'AgroVision';
      default:
        return provider;
    }
  }

  getModalDescription(): string {
    switch (this.data.provider) {
      case RvoProvider.RvO:
        return this.locale.translate('retrieveCropFieldsExplanationForRvO');
      case RvoProvider.RvORegulatoryParcelManure:
        return this.locale.translate('retrieveCropFieldsExplanationForRvORegulatoryManureFields');
      default:
        return this.locale.translate('retrieveCropFieldsExplanation');
    }
  }

  getPlaceholderForFarmId(): string {
    const kvkNumber = this.formGroup.controls.farmId;
    return this.withRvoProvider
      ? kvkNumber.value
        ? this.locale.translate('kvkNumber')
        : this.locale.translate('fillInKvkNumber')
      : this.locale.translate('externalId');
  }

  private updateCustomers = (): void => {
    const provider = this.formGroup.controls.provider.value as AgroConnectExternalParty | null;
    const year = this.formGroup.controls.year.value;

    if (!this.withRvoProvider) {
      if (provider && year) {
        this.store.dispatch(new LoadAgroConnectCustomersRequest(provider, year));
      }

      this.formGroup.controls.externalCustomer.setValue('');
    }
  };

  private filter(value: string, items: AgroConnectCustomer[]) {
    return [...items].filter((option) =>
      !option.farmDesignator
        ? false
        : option.farmDesignator.toLowerCase().includes(value.toLowerCase()),
    );
  }

  private removeDuplicateCustomers(items: AgroConnectCustomer[]): AgroConnectCustomer[] {
    return items.filter(
      (v, i, a) =>
        a.findIndex((t) => (!t?.farmId || !v?.farmId ? -1 : t.farmId === v.farmId)) === i,
    );
  }

  private prefillKvkNumberForRvo(customer: SelectedCustomer) {
    const now = new Date();
    const kvkNumber = customer.shipTos
      .find((x) => x.externalId === customer.externalId)
      ?.identifications.find(
        (i) =>
          i.name === ShipToIdentification.BUP002 &&
          this.isIdentificationCurrentlyValid(i.validFrom, i.validTo, now),
      )?.value;

    const kvkNumberFormControl = this.formGroup.controls.farmId;
    kvkNumberFormControl.setValue(kvkNumber);
    kvkNumberFormControl.setValidators([Validators.required, kvkNumberValidator]);
    kvkNumberFormControl.updateValueAndValidity();
  }

  private isIdentificationCurrentlyValid(validFrom: string, validTo: string, now: Date): boolean {
    const validFromDate = new Date(validFrom);
    const validToDate = new Date(validTo);

    return validFromDate <= now && validToDate >= now;
  }

  private prefillCustomerNameForRvo(customer: SelectedCustomer) {
    const customerFormControl = this.formGroup.controls.externalCustomer;
    const kvkNumberFormControl = this.formGroup.controls.farmId;
    const kvkNumber = kvkNumberFormControl.value ?? '';

    this.customers = kvkNumber
      ? [
          {
            farmId: kvkNumber,
            farmDesignator: customer.name,
            postCode: '',
            streetName: '',
            cityName: '',
          },
        ]
      : [];

    customerFormControl.setValue(kvkNumber);
  }
}
